import React, { useEffect, useMemo, useRef, useState } from "react";
import { useGetBookingDates } from "../../APICalling/useApiQueries";
import {
  buildPaxesParam,
  getCurrentDateInSingapore,
  getDateAfterDays,
} from "../../Utils/Helper";
import { DateTabsCarousel } from "./DateTabsCarousel";
import BookingDatesPickerLoader from "./BookingDatesPickerLoader";

const DAYS_PER_WINDOW = 7;
const MAX_WINDOWS = 2;
const MAX_WINDOW_INDEX = MAX_WINDOWS - 1;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function parseDdMmYyyy(dateStr) {
  if (!dateStr) return NaN;
  const [day, month, year] = dateStr.split("-").map(Number);
  if (!day || !month || !year) return NaN;
  return Date.UTC(year, month - 1, day);
}

function daysBetween(fromStr, toStr) {
  const from = parseDdMmYyyy(fromStr);
  const to = parseDdMmYyyy(toStr);
  if (Number.isNaN(from) || Number.isNaN(to)) return 0;
  return Math.round((to - from) / MS_PER_DAY);
}

function windowIndexForDate(fromStr, toStr) {
  const diff = daysBetween(fromStr, toStr);
  if (diff <= 0) return 0;
  return Math.min(MAX_WINDOW_INDEX, Math.floor(diff / DAYS_PER_WINDOW));
}

export const BookingDatesPicker = ({
  selectedOutletID,
  members = [],
  selectedDate,
  onDateSelect,
  onAutoDateSelect = onDateSelect,
  formatDate,
  className = "",
  onLoadingChange = () => {}, // Notify parent when dates API is loading
  onAvailabilityChange,
  rescheduling = false,
  uuid = null,
}) => {
  const hasAlignedInitialDateRef = useRef(false);
  const userNavigatedRef = useRef(false);
  const navigationLockRef = useRef(false);
  const pendingSelectionWindowRef = useRef(null);
  const lastSuccessfulWindowRef = useRef(0);
  const selectedDateRef = useRef(selectedDate);
  selectedDateRef.current = selectedDate;

  const currentDate = useMemo(() => getCurrentDateInSingapore(), []);
  const [windowIndex, setWindowIndex] = useState(() =>
    windowIndexForDate(currentDate, selectedDate)
  );
  const [reachedEnd, setReachedEnd] = useState(false);

  const paxesParam = useMemo(
    () => buildPaxesParam(members),
    [members]
  );

  const startDate = useMemo(
    () => getDateAfterDays(currentDate, windowIndex * DAYS_PER_WINDOW),
    [currentDate, windowIndex]
  );
  const endDate = useMemo(
    () => getDateAfterDays(currentDate, windowIndex * DAYS_PER_WINDOW + (DAYS_PER_WINDOW - 1)),
    [currentDate, windowIndex]
  );

  const bookingDatesParams = useMemo(() => ({
    panel: "customer",
    outlet: selectedOutletID,
    start_date: startDate,
    end_date: endDate,
    paxes: paxesParam,
    ...(rescheduling && {
      type: "reschedule",
      uuid,
    }),
  }), [selectedOutletID, startDate, endDate, paxesParam, rescheduling, uuid]);

  const {
    data: bookingDatesResponse,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetBookingDates(bookingDatesParams, {
    enabled: !!selectedOutletID && windowIndex <= MAX_WINDOW_INDEX,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const dates = useMemo(
    () => bookingDatesResponse?.data || [],
    [bookingDatesResponse]
  );

  const dateKeys = useMemo(
    () => (Array.isArray(dates) ? dates : Object.keys(dates || {})),
    [dates]
  );

  const slotAvailabilityByDate = useMemo(
    () =>
      Array.isArray(dates)
        ? {}
        : Object.entries(dates || {}).reduce((acc, [date, value]) => {
            acc[date] = value?.slot_available;
            return acc;
          }, {}),
    [dates]
  );

  const isWindowPending = !bookingDatesResponse && (isLoading || isFetching);
  const firstAvailableDate = dateKeys.find(
    (date) => slotAvailabilityByDate[date] === true
  );
  const isSelectingNavigatedDate = pendingSelectionWindowRef.current === windowIndex &&
    !isError && Boolean(firstAvailableDate) && firstAvailableDate !== selectedDate;
  const isSelectingInitialDate = !hasAlignedInitialDateRef.current && !isError &&
    slotAvailabilityByDate[selectedDate] !== true && Boolean(firstAvailableDate) &&
    firstAvailableDate !== selectedDate;
  useEffect(() => {
    onAvailabilityChange?.({ date: selectedDate, available: !isError &&
      slotAvailabilityByDate[selectedDate] === true,
      byDate: slotAvailabilityByDate,
      // A failed dates request must surface as an error with retry, not as "No Availability".
      errorMessage: isError
        ? error?.response?.data?.message || "Unable to load available dates. Please try again."
        : "" });
  }, [selectedDate, slotAvailabilityByDate, isError, error, onAvailabilityChange]);

  useEffect(() => {
    hasAlignedInitialDateRef.current = false;
    userNavigatedRef.current = false;
    navigationLockRef.current = false;
    pendingSelectionWindowRef.current = null;
    lastSuccessfulWindowRef.current = 0;
    setReachedEnd(false);
    setWindowIndex(windowIndexForDate(currentDate, selectedDateRef.current));
  }, [selectedOutletID, paxesParam, currentDate]);

  // Open the window that contains a restored/rescheduled date before the user navigates.
  useEffect(() => {
    if (userNavigatedRef.current) return;
    const target = windowIndexForDate(currentDate, selectedDate);
    setWindowIndex((prev) => (prev !== target ? target : prev));
  }, [selectedDate, currentDate]);

  // Wait for new windows and automatic selection, retaining resolved results
  // during background refreshes of the current window.
  useEffect(() => {
    onLoadingChange(isWindowPending || isSelectingNavigatedDate || isSelectingInitialDate);
  }, [isWindowPending, isSelectingNavigatedDate, isSelectingInitialDate, onLoadingChange]);

  useEffect(() => {
    if (!isFetching) {
      navigationLockRef.current = false;
    }
  }, [isFetching, windowIndex]);

  useEffect(() => {
    if (isLoading || isFetching) return;

    if (isError) {
      if (windowIndex !== lastSuccessfulWindowRef.current) {
        setWindowIndex(lastSuccessfulWindowRef.current);
      }
      navigationLockRef.current = false;
      return;
    }

    if (dateKeys.length > 0) {
      lastSuccessfulWindowRef.current = windowIndex;
      return;
    }

    if (windowIndex !== lastSuccessfulWindowRef.current) {
      setReachedEnd(true);
      setWindowIndex(lastSuccessfulWindowRef.current);
      navigationLockRef.current = false;
    }
  }, [isLoading, isFetching, isError, dateKeys.length, windowIndex]);

  // Paging is a new date choice, even if the previous date was restored/manual.
  // Wait for this window's availability before choosing its first open day.
  useEffect(() => {
    if (pendingSelectionWindowRef.current !== windowIndex || isLoading || isFetching) return;
    pendingSelectionWindowRef.current = null;
    if (isError || !bookingDatesResponse) return;

    const firstAvailableDate = dateKeys.find(
      (date) => slotAvailabilityByDate[date] === true
    );
    if (firstAvailableDate && firstAvailableDate !== selectedDate) {
      onAutoDateSelect(firstAvailableDate);
    }
  }, [windowIndex, isLoading, isFetching, isError, bookingDatesResponse,
    dateKeys, slotAvailabilityByDate, selectedDate, onAutoDateSelect]);

  useEffect(() => {
    if (isLoading || isFetching || isError || hasAlignedInitialDateRef.current || dateKeys.length === 0) return;

    if (Array.isArray(dates)) {
      hasAlignedInitialDateRef.current = true;
      return;
    }

    const selectedDateInRange = dateKeys.includes(selectedDate);
    const selectedDateHasSlots = slotAvailabilityByDate[selectedDate] === true;

    // Preserve a restored/manual date only while it still has availability.
    // An unavailable saved date must not prevent selecting the first open day.
    if (selectedDateInRange && selectedDateHasSlots) {
      hasAlignedInitialDateRef.current = true;
      return;
    }

    const firstDateWithSlots = dateKeys.find(
      (date) => slotAvailabilityByDate[date] === true
    );

    if (firstDateWithSlots && firstDateWithSlots !== selectedDate) {
      onAutoDateSelect(firstDateWithSlots);
    }

    hasAlignedInitialDateRef.current = true;
  }, [
    isLoading,
    isFetching,
    isError,
    dates,
    dateKeys,
    slotAvailabilityByDate,
    selectedDate,
    onAutoDateSelect,
  ]);

  const canLoadNext =
    windowIndex < MAX_WINDOW_INDEX &&
    !(reachedEnd && windowIndex >= lastSuccessfulWindowRef.current);

  const requestNextWindow = () => {
    if (navigationLockRef.current || isWindowPending || !canLoadNext) return;
    navigationLockRef.current = true;
    userNavigatedRef.current = true;
    pendingSelectionWindowRef.current = windowIndex + 1;
    setWindowIndex((index) => Math.min(MAX_WINDOW_INDEX, index + 1));
  };

  const requestPrevWindow = () => {
    if (navigationLockRef.current || isWindowPending || windowIndex <= 0) return;
    navigationLockRef.current = true;
    userNavigatedRef.current = true;
    pendingSelectionWindowRef.current = windowIndex - 1;
    setWindowIndex((index) => Math.max(0, index - 1));
  };

  return (
    <div className={className}>
      {isWindowPending ? (
        <BookingDatesPickerLoader />
      ) : (
        dateKeys.length > 0 && (
        <DateTabsCarousel
          key={startDate}
          dates={dateKeys}
          slotAvailabilityByDate={slotAvailabilityByDate}
          selectedDate={selectedDate}
          onDateSelect={onDateSelect}
          onAutoDateSelect={onAutoDateSelect}
          formatDate={formatDate}
          canLoadNext={canLoadNext}
          canLoadPrev={windowIndex > 0}
          onNeedNextDates={requestNextWindow}
          onNeedPrevDates={requestPrevWindow}
        />
        )
      )}
    </div>
  );
};
